/**
 * Scopes der externen API. Server (Auth, Zod) und Client (Einstellungen) teilen diese Liste.
 * Ein neuer Scope braucht zusätzlich eine Migration: der CHECK auf api_keys.scopes zählt die Werte auf.
 */
export const API_SCOPES = [
  'projects:read',
  'projects:write',
  'offers:read',
  'offers:write',
  'documents:write',
] as const;

export type ApiScope = (typeof API_SCOPES)[number];

export function isApiScope(value: unknown): value is ApiScope {
  return typeof value === 'string' && (API_SCOPES as readonly string[]).includes(value);
}

export const API_SCOPE_LABELS_DE: Record<ApiScope, string> = {
  'projects:read': 'Projekte lesen',
  'projects:write': 'Projekte anlegen und ergänzen',
  'offers:read': 'Angebote und Positionen lesen',
  'offers:write': 'Angebote anlegen und Positionen bearbeiten',
  'documents:write': 'Dokumente und Fotos hochladen',
};

export type ApiScopePresetId = 'readonly' | 'read_plus_create_projects' | 'full';

export const API_SCOPE_PRESETS: ReadonlyArray<{ id: ApiScopePresetId; label: string; scopes: readonly ApiScope[] }> = [
  { id: 'readonly', label: 'Nur lesen', scopes: ['projects:read', 'offers:read'] },
  {
    id: 'read_plus_create_projects',
    label: 'Lesen + Projekte anlegen',
    scopes: ['projects:read', 'offers:read', 'projects:write', 'documents:write'],
  },
  { id: 'full', label: 'Voller Zugriff', scopes: [...API_SCOPES] },
];

/** Dedupliziert in Katalogreihenfolge; null bei leerer Liste oder unbekanntem Wert. */
export function normalizeScopes(input: unknown): ApiScope[] | null {
  if (!Array.isArray(input)) return null;
  const set = new Set<ApiScope>();
  for (const raw of input) {
    if (!isApiScope(raw)) return null;
    set.add(raw);
  }
  if (set.size === 0) return null;
  return API_SCOPES.filter((scope) => set.has(scope));
}

export function hasApiScope(scopes: readonly string[], scope: ApiScope): boolean {
  return scopes.includes(scope);
}
